// Lightweight markdown to HTML parser
export function parseMarkdown(md) {
  if (!md) return '';

  let html = md;

  // Escape HTML entities first
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Fenced code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre><code class="lang-${lang}">${code.trim()}</code></pre>`;
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Horizontal rule
  html = html.replace(/^---$/gm, '<hr>');

  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');

  // Bold + italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Unordered lists
  html = html.replace(/((?:^[-*] .+\n?)+)/gm, (block) => {
    const items = block.trim().split('\n').map(line =>
      `<li>${line.replace(/^[-*] /, '')}</li>`
    ).join('');
    return `<ul>${items}</ul>`;
  });

  // Ordered lists
  html = html.replace(/((?:^\d+\. .+\n?)+)/gm, (block) => {
    const items = block.trim().split('\n').map(line =>
      `<li>${line.replace(/^\d+\. /, '')}</li>`
    ).join('');
    return `<ol>${items}</ol>`;
  });

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%;border-radius:4px;margin:1rem 0;">');

  // Paragraphs: wrap lines that aren't already block elements
  const lines = html.split('\n');
  const result = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    if (/^<(h[1-6]|ul|ol|pre|blockquote|hr|img)/.test(trimmed)) {
      result.push(trimmed);
      i++;
    } else {
      // Gather paragraph lines
      const para = [];
      while (i < lines.length && lines[i].trim() && !/^<(h[1-6]|ul|ol|pre|blockquote|hr|img)/.test(lines[i].trim())) {
        para.push(lines[i].trim());
        i++;
      }
      if (para.length) result.push(`<p>${para.join(' ')}</p>`);
    }
  }

  return result.join('\n');
}

// Extract title from markdown (first h1 or filename)
export function extractTitle(md, filename) {
  const h1 = md.match(/^# (.+)$/m);
  if (h1) return h1[1];
  return filename.replace(/\.md$/i, '').replace(/[-_]/g, ' ');
}

// Extract date from markdown frontmatter or filename
export function extractDate(md, filename) {
  // Try frontmatter date
  const frontmatter = md.match(/^---\n([\s\S]*?)\n---/);
  if (frontmatter) {
    const dateMatch = frontmatter[1].match(/date:\s*(.+)/);
    if (dateMatch) return new Date(dateMatch[1].trim());
  }
  // Try date in filename e.g. 2024-03-15-my-post.md
  const fileDate = filename.match(/^(\d{4}-\d{2}-\d{2})/);
  if (fileDate) return new Date(fileDate[1]);
  return new Date();
}

export function formatDate(date) {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Extract plain-text excerpt — first non-heading, non-frontmatter paragraph, truncated
export function extractExcerpt(md, maxLength = 120) {
  if (!md) return '';
  // Strip frontmatter
  const stripped = md.replace(/^---\n[\s\S]*?\n---\n?/, '');
  // Split into lines, find first paragraph (non-empty, not a heading, not a list)
  const lines = stripped.split('\n');
  let para = '';
  for (const line of lines) {
    const t = line.trim();
    if (!t || t.startsWith('#') || t.startsWith('-') || t.startsWith('*') || t.startsWith('>') || t.startsWith('```')) continue;
    para = t;
    break;
  }
  // Strip markdown syntax
  para = para.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/`(.+?)`/g, '$1');
  if (para.length <= maxLength) return para;
  return para.slice(0, maxLength).replace(/\s+\S*$/, '') + '…';
}
